package com.tovtalk.data.remote.firebase

import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.repository.AuthManager
import com.tovtalk.utils.logger.Logger
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlin.coroutines.suspendCoroutine

private fun fromWindow(key: String): String? {
    val env = window.asDynamic().__ENV__
    return if (env != null && env[key] != null) env[key] as String else null
}

private fun getFirebaseConfig(): dynamic {
    val apiKey = fromWindow("FIREBASE_API_KEY") ?: return null
    val authDomain = fromWindow("FIREBASE_AUTH_DOMAIN")
    val projectId = fromWindow("FIREBASE_PROJECT_ID")
    val appId = fromWindow("FIREBASE_APP_ID")
    val config = js("{}").unsafeCast<dynamic>()
    config.apiKey = apiKey
    config.authDomain = authDomain
    config.projectId = projectId
    config.appId = appId
    return config
}

/**
 * Return the default Firebase app, initializing it only if no app exists yet. Prevents
 * "Firebase App named '[DEFAULT]' already exists" when multiple modules (auth, FCM, Firestore)
 * each try to initialize. Shared across jsMain Firebase glue.
 */
internal fun getOrInitFirebaseApp(config: dynamic): dynamic =
    if (getApps().isNotEmpty()) getApp() else initializeApp(config)

private suspend fun awaitPromise(promise: dynamic): dynamic = suspendCoroutine { cont ->
    val onFulfilled: (dynamic) -> Unit = { value -> cont.resume(value) }
    val onRejected: (dynamic) -> Unit = { err -> cont.resumeWithException(Throwable(err?.toString() ?: "Unknown error")) }
    js("(function(p, ok, err) { p.then(ok, err); })").unsafeCast<(dynamic, (dynamic) -> Unit, (dynamic) -> Unit) -> Unit>()(promise, onFulfilled, onRejected)
}

class JsFirebaseAuthManager : AuthManager {
    private var _auth: dynamic = null
    private var _app: dynamic = null
    private var redirectResultConsumed: Boolean = false
    private val redirectResultMutex = Mutex()

    private fun ensureInit(): dynamic {
        if (_auth != null) return _auth
        val config = getFirebaseConfig()
        if (config == null) return null
        _app = getOrInitFirebaseApp(config)
        _auth = getAuth(_app)
        return _auth
    }

    override suspend fun getRedirectResultFirebaseTokenIfAny(): DomainResult<String>? = redirectResultMutex.withLock {
        Logger.d("GoogleRedirect: getRedirectResultFirebaseTokenIfAny ENTER redirectResultConsumed=$redirectResultConsumed", "GoogleRedirect")
        if (redirectResultConsumed) {
            Logger.d("GoogleRedirect: getRedirectResultFirebaseTokenIfAny SKIP (already consumed)", "GoogleRedirect")
            return@withLock null
        }
        val auth = ensureInit()
        if (auth == null) {
            Logger.d("GoogleRedirect: getRedirectResultFirebaseTokenIfAny SKIP (auth not inited)", "GoogleRedirect")
            return@withLock null
        }
        return@withLock try {
            val hash = window.location.hash
            Logger.d("GoogleRedirect: getRedirectResultFirebaseTokenIfAny hash.length=${hash.length} hashPreview=${hash.take(100)}", "GoogleRedirect")
            if (hash.isNotEmpty() && (hash.contains("auth") || hash.contains("__"))) {
                Logger.d("GoogleRedirect: getRedirectResultFirebaseTokenIfAny delaying 400ms for SDK", "GoogleRedirect")
                delay(400)
            }
            Logger.d("GoogleRedirect: getRedirectResultFirebaseTokenIfAny calling getRedirectResult(auth)", "GoogleRedirect")
            val resultPromise = getRedirectResult(auth)
            val result = awaitPromise(resultPromise)
            if (result == null) {
                Logger.d("GoogleRedirect: getRedirectResultFirebaseTokenIfAny getRedirectResult returned null", "GoogleRedirect")
                return@withLock null
            }
            Logger.d("GoogleRedirect: getRedirectResultFirebaseTokenIfAny got result, extracting user", "GoogleRedirect")
            val user = js("(function(r) { return r && r.user; })").unsafeCast<(Any?) -> Any?>()(result)
            if (user == null) {
                Logger.d("GoogleRedirect: getRedirectResultFirebaseTokenIfAny result has no user", "GoogleRedirect")
                return@withLock null
            }
            val tokenResult = js("(function(u) { return u.getIdToken(false); })").unsafeCast<(Any?) -> dynamic>()(user)
            val token = awaitPromise(tokenResult)
            val tokenStr = token?.toString()
            if (tokenStr != null) {
                redirectResultConsumed = true
                Logger.d("GoogleRedirect: getRedirectResultFirebaseTokenIfAny SUCCESS token.length=${tokenStr.length}", "GoogleRedirect")
                DomainResult.Success(tokenStr)
            } else {
                Logger.d("GoogleRedirect: getRedirectResultFirebaseTokenIfAny getIdToken returned null", "GoogleRedirect")
                null
            }
        } catch (e: Throwable) {
            Logger.e("GoogleRedirect: getRedirectResultFirebaseTokenIfAny ERROR ${e.message}", "GoogleRedirect", e)
            null
        }
    }

    override suspend fun signInWithGoogleAndGetFirebaseToken(googleIdToken: String, googleAccessToken: String?): DomainResult<String> {
        Logger.d("GooglePopup: signInWithGoogleAndGetFirebaseToken ENTER (user clicked Google button)", "GooglePopup")
        val auth = ensureInit()
        if (auth == null) {
            Logger.e("GooglePopup: Firebase not configured", "GooglePopup")
            return DomainResult.Error("Firebase not configured for Web. Set FIREBASE_API_KEY, FIREBASE_AUTH_DOMAIN, FIREBASE_PROJECT_ID, FIREBASE_APP_ID in window.__ENV__")
        }
        return try {
            val provider = GoogleAuthProvider()
            js("(function(p){ p.setCustomParameters({prompt:'select_account'}); })").unsafeCast<(GoogleAuthProvider) -> Unit>()(provider)
            Logger.d("GooglePopup: opening signInWithPopup", "GooglePopup")
            val resultPromise = signInWithPopup(auth, provider)
            val result = awaitPromise(resultPromise)
            val user = js("(function(r) { return r && r.user; })").unsafeCast<(Any?) -> Any?>()(result)
            if (user == null) {
                Logger.e("GooglePopup: popup result has no user", "GooglePopup")
                return DomainResult.Error("Google sign-in returned no user")
            }
            val tokenResult = js("(function(u) { return u.getIdToken(false); })").unsafeCast<(Any?) -> dynamic>()(user)
            val token = awaitPromise(tokenResult)
            val tokenStr = token?.toString()
            if (tokenStr != null) {
                Logger.d("GooglePopup: SUCCESS token.length=${tokenStr.length}", "GooglePopup")
                DomainResult.Success(tokenStr)
            } else {
                DomainResult.Error("Failed to get Firebase token")
            }
        } catch (e: Throwable) {
            Logger.e("GooglePopup: signInWithPopup ERROR ${e.message}", "GooglePopup", e)
            DomainResult.Error(e.message ?: "Google Sign-In failed")
        }
    }

    override suspend fun signInWithEmailAndPassword(email: String, password: String): DomainResult<Unit> {
        val auth = ensureInit()
        if (auth == null) return DomainResult.Error("Firebase not configured for Web")
        return try {
            awaitPromise(com.tovtalk.data.remote.firebase.signInWithEmailAndPassword(auth, email, password))
            DomainResult.Success(Unit)
        } catch (e: Throwable) {
            DomainResult.Error(e.message ?: "Email sign-in failed")
        }
    }

    override suspend fun signInWithCustomToken(customToken: String): DomainResult<Unit> {
        val auth = ensureInit()
        if (auth == null) return DomainResult.Error("Firebase not configured for Web")
        return try {
            awaitPromise(com.tovtalk.data.remote.firebase.signInWithCustomToken(auth, customToken))
            DomainResult.Success(Unit)
        } catch (e: Throwable) {
            DomainResult.Error(e.message ?: "Custom token sign-in failed")
        }
    }

    override suspend fun getFirebaseIdToken(): DomainResult<String> {
        val auth = ensureInit()
        if (auth == null) return DomainResult.Error("Firebase not configured for Web")
        val user = js("(function(a) { return a && a.currentUser; })").unsafeCast<(dynamic) -> dynamic>()(auth)
        if (user == null) return DomainResult.Error("No authenticated user")
        return try {
            // forceRefresh=true: this is the session-restore/exchange path (token is
            // immediately swapped for backend tokens). After a page refresh the cached
            // token may be near-expiry and would 401 at the backend, so mint a fresh one.
            val tokenResult = user.getIdToken(true)
            val token = awaitPromise(tokenResult)
            val tokenStr = token?.toString()
            if (tokenStr != null) DomainResult.Success(tokenStr) else DomainResult.Error("Failed to get token")
        } catch (e: Throwable) {
            DomainResult.Error(e.message ?: "Failed to get token")
        }
    }

    override fun signOut() {
        val auth = _auth ?: return
        com.tovtalk.data.remote.firebase.signOut(auth)
    }

    override fun getCurrentUserId(): String? {
        val auth = _auth ?: ensureInit() ?: return null
        val user = js("(function(a) { return a && a.currentUser; })").unsafeCast<(dynamic) -> dynamic>()(auth) ?: return null
        return user.uid as? String
    }

    /** Expose Firebase app for Firestore (e.g. chat). Returns null if Firebase not configured. */
    fun getApp(): dynamic {
        ensureInit()
        return _app
    }
}
